import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Copy,
  KeyRound,
  Lock,
  Plus,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import {
  API_KEY_SCOPES,
  API_KEY_SCOPE_META,
  type ApiKey,
  type ApiKeyScope,
  type CreateApiKeyResult,
} from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { SERVER_URL } from '../lib/constants.js';
import { useAuthStore } from '../stores/authStore.js';
import { formatDate } from '../lib/billing-format.js';

// ─────────────────────────────────────────────────────────────────
// API access — the workspace's API keys.
//
// A key is a *pair*: the key (public, shown here forever) and the secret
// (returned exactly once, by the create request). Everything about this page
// follows from that asymmetry — the list can show keys freely, while a new
// key's secret is surfaced in a copy-once panel that says so out loud.
//
// Keys are workspace-wide credentials, so the page is owner/admin only, and
// the server enforces the same bar (a member sees the "Owners only" state
// below rather than a page whose buttons would 403).
// ─────────────────────────────────────────────────────────────────

const SCOPE_ICON_CLASS = 'text-muted';

export function ApiAccessPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const qc = useQueryClient();

  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces.list(),
  });
  const workspace = workspacesData?.workspaces.find((w) => w.id === activeWorkspaceId);
  const myRole = workspace?.role ?? 'member';
  const canManage = myRole === 'owner' || myRole === 'admin';

  const keysQuery = useQuery({
    queryKey: ['workspace', 'api-keys', activeWorkspaceId],
    queryFn: () => api.workspaces.apiKeys(activeWorkspaceId!),
    enabled: Boolean(activeWorkspaceId) && canManage,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['workspace', 'api-keys', activeWorkspaceId] });
  };

  const revoke = async (key: ApiKey) => {
    setError(null);
    try {
      await api.workspaces.revokeApiKey(activeWorkspaceId!, key.id);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!canManage) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <Lock size={22} className="text-muted" />
        <h2 className="text-ink text-base font-semibold">Owners only</h2>
        <p className="text-muted max-w-sm text-[13px]">
          API keys are credentials for the whole workspace, so only the owner or an admin can
          manage them. Ask them to issue one for you.
        </p>
        <Link to="/posts" className="btn-ghost mt-4 px-4 py-2 text-[13px]">
          <ArrowLeft size={14} />
          Back to posts
        </Link>
      </div>
    );
  }

  const keys = keysQuery.data?.data ?? [];
  const activeCount = keys.filter((k) => k.isActive).length;

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 px-8 pt-8 pb-6">
        <div>
          <p className="kicker mb-2">
            <span className="bg-accent h-1.5 w-1.5 rounded-full" />
            Workspace settings
          </p>
          <h1 className="text-ink text-[22px] font-bold tracking-tight">API access</h1>
          <p className="text-ink-soft mt-1 max-w-2xl text-[13px]">
            {workspace?.name ?? 'This workspace'} — issue scoped key pairs so an external system
            can queue sends and manage subscribers without a user session.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          <Plus size={15} strokeWidth={2.4} />
          Create key
        </button>
      </div>

      {error && (
        <p className="border-danger/20 bg-danger/8 text-danger mb-4 mx-8 rounded-lg border px-3 py-2 text-[12.5px]">
          {error}
        </p>
      )}

      {/* ── How to authenticate ─────────────────────────────── */}
      <div className="border-line bg-card mx-8 mb-6 rounded-xl border p-4">
        <h3 className="text-ink mb-1 text-[13px] font-semibold">Authenticating</h3>
        <p className="text-muted mb-3 text-[12.5px]">
          Send the pair on every request. The key names the credential; the secret is what proves
          it. Full documentation is in{' '}
          <a
            href={`${SERVER_URL}/docs`}
            target="_blank"
            rel="noreferrer"
            className="text-accent font-semibold hover:text-ink"
          >
            the API reference
          </a>
          .
        </p>
        <pre className="border-line bg-card-2 text-ink-soft overflow-x-auto rounded-lg border p-3 font-mono text-[11px] leading-relaxed">
          {`Authorization: Bearer <key>.<secret>

# or as two headers
X-Api-Key: <key>
X-Api-Secret: <secret>`}
        </pre>
      </div>

      {/* ── Keys ────────────────────────────────────────────── */}
      <h2 className="text-ink mb-3 px-8 text-[15px] font-bold tracking-tight">
        Keys
        <span className="text-muted ml-2 text-[12px] font-medium">
          {activeCount} active{keys.length > activeCount ? `, ${keys.length} total` : ''}
        </span>
      </h2>

      {keysQuery.isLoading ? (
        <div className="text-muted flex h-32 items-center justify-center gap-2 px-8 text-sm">
          <span className="border-line-strong border-t-accent h-3.5 w-3.5 animate-spin rounded-full border-2" />
          Loading keys…
        </div>
      ) : keys.length === 0 ? (
        <div className="border-line bg-card mx-8 rounded-xl border border-dashed p-8 text-center">
          <KeyRound size={20} className="text-muted mx-auto mb-2" />
          <p className="text-ink text-[13px] font-semibold">No API keys yet</p>
          <p className="text-muted mx-auto mt-1 max-w-md text-[12.5px]">
            Create one to let an external system queue sends or manage subscribers. Each key does
            only what you tick for it.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5 px-8">
          {keys.map((key) => (
            <KeyRow key={key.id} apiKey={key} onRevoke={() => void revoke(key)} />
          ))}
        </div>
      )}

      {/* ── Scopes reference ────────────────────────────────── */}
      <h2 className="text-ink mt-8 mb-3 px-8 text-[15px] font-bold tracking-tight">
        Permissions
      </h2>
      <div className="space-y-2 px-8 pb-10">
        {API_KEY_SCOPES.map((scope) => (
          <div key={scope} className="surface-row flex items-start gap-3 px-4 py-3">
            <ShieldAlert size={15} className={`mt-0.5 shrink-0 ${SCOPE_ICON_CLASS}`} />
            <div className="min-w-0">
              <p className="text-ink text-[13px] font-semibold">{API_KEY_SCOPE_META[scope].label}</p>
              <p className="text-muted mt-0.5 text-[12px]">
                {API_KEY_SCOPE_META[scope].description}
              </p>
              <p className="text-muted mt-1 font-mono text-[11px]">
                {scope} · {API_KEY_SCOPE_META[scope].endpoint}
              </p>
            </div>
          </div>
        ))}
      </div>

      {createOpen && (
        <CreateKeyModal
          workspaceId={activeWorkspaceId!}
          onClose={() => setCreateOpen(false)}
          onCreated={refresh}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// One key row
// ─────────────────────────────────────────────────────────────────

function KeyRow({ apiKey, onRevoke }: { apiKey: ApiKey; onRevoke: () => void }) {
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const copyKey = async () => {
    await navigator.clipboard.writeText(apiKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="surface-row px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="bg-ink/5 text-ink-soft flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
          <KeyRound size={15} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-ink flex items-center gap-2 truncate text-[13.5px] font-semibold">
            {apiKey.name}
            <span
              className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                apiKey.isActive
                  ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
                  : 'bg-ink/5 text-muted'
              }`}
            >
              {apiKey.revokedAt ? 'revoked' : apiKey.isActive ? 'active' : 'expired'}
            </span>
          </p>
          <p className="text-muted mt-0.5 truncate font-mono text-[11.5px]">{apiKey.key}</p>
        </div>

        <button
          type="button"
          onClick={() => void copyKey()}
          className="btn-ghost px-2.5 py-1.5 text-[12px]"
          aria-label={`Copy key ${apiKey.name}`}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy key'}
        </button>

        {apiKey.isActive && (
          <button
            type="button"
            onClick={() => (confirming ? onRevoke() : setConfirming(true))}
            onBlur={() => setConfirming(false)}
            className={`btn-ghost px-2.5 py-1.5 text-[12px] ${
              confirming ? 'text-danger' : 'text-muted'
            }`}
          >
            <Trash2 size={13} />
            {confirming ? 'Confirm revoke' : 'Revoke'}
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {apiKey.scopes.map((scope) => (
          <span key={scope} className="text-ink-soft text-[11.5px] font-medium">
            {API_KEY_SCOPE_META[scope].label}
          </span>
        ))}
        <span className="text-muted ml-auto text-[11px]">
          Created {formatDate(apiKey.createdAt)}
          {apiKey.createdByEmail ? ` by ${apiKey.createdByEmail}` : ''}
          {' · '}
          {apiKey.lastUsedAt ? `Last used ${formatDate(apiKey.lastUsedAt)}` : 'Never used'}
          {apiKey.expiresAt
            ? ` · ${apiKey.isActive ? 'Expires' : 'Expired'} ${formatDate(apiKey.expiresAt)}`
            : ''}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Create modal — form, then the one-and-only secret panel
// ─────────────────────────────────────────────────────────────────

function CreateKeyModal({
  workspaceId,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateApiKeyResult | null>(null);
  const [copied, setCopied] = useState(false);

  const toggle = (scope: ApiKeyScope) =>
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );

  const submit = async () => {
    if (!name.trim() || scopes.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.workspaces.createApiKey(workspaceId, {
        name: name.trim(),
        scopes,
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      });
      setCreated(result);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.authorization);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-h-[90vh] w-full max-w-md p-5">
        {created ? (
          <div>
            <div className="bg-accent/10 text-accent mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl">
              <Check size={20} />
            </div>
            <h3 className="text-ink text-center text-[15px] font-bold">
              “{created.apiKey.name}” is ready
            </h3>
            <p className="text-muted mt-1 text-center text-[12.5px]">
              Copy the secret now — it is shown once and only its hash is stored. If you lose it,
              revoke this key and create another.
            </p>

            <label className="field-label mt-4 block">Authorization header</label>
            <pre className="border-line bg-card-2 text-ink-soft overflow-x-auto rounded-lg border p-3 font-mono text-[11px] leading-relaxed">
              {created.authorization}
            </pre>

            <button
              type="button"
              onClick={() => void copySecret()}
              className="btn-primary mt-3 w-full justify-center px-3 py-2 text-[13px]"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied to clipboard' : 'Copy header'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost mt-2 w-full justify-center px-3 py-2 text-[13px]"
            >
              I have stored it
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-ink text-[15px] font-bold tracking-tight">Create API key</h3>
                <p className="text-muted mt-0.5 text-[12px]">
                  Grant only what the integration needs.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="btn-ghost h-8 w-8 rounded-lg px-0"
              >
                ✕
              </button>
            </div>

            <label className="field-label mt-4 block">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Website signup form"
              className="field-input w-full"
            />

            <label className="field-label mt-3 block">Permissions</label>
            <div className="space-y-2">
              {API_KEY_SCOPES.map((scope) => (
                <label
                  key={scope}
                  className="border-line hover:bg-ink/[0.02] flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5"
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={() => toggle(scope)}
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="text-ink block text-[12.5px] font-semibold">
                      {API_KEY_SCOPE_META[scope].label}
                    </span>
                    <span className="text-muted mt-0.5 block text-[11.5px]">
                      {API_KEY_SCOPE_META[scope].description}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <label className="field-label mt-3 block">Expires (optional)</label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="field-input w-full"
            />
            <p className="text-muted mt-1 text-[11.5px]">
              Leave empty for a key that works until you revoke it.
            </p>

            {error && (
              <p className="border-danger/20 bg-danger/8 text-danger mt-3 rounded-lg border px-3 py-2 text-[12px]">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || !name.trim() || scopes.length === 0}
              className="btn-primary mt-4 w-full justify-center px-3 py-2 text-[13px]"
            >
              <KeyRound size={14} />
              {busy ? 'Creating…' : 'Create key'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
